import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './CartingPerformance.css';

/**
 * Carting Performance Dashboard
 * Shows timing metrics by vessel type and operator
 */
function CartingPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [department, setDepartment] = useState('carting');

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/departments/${department}/vessel-performance?dateRange=${dateRange}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching performance:', error);
    }
    setLoading(false);
  }, [department, dateRange]);

  useEffect(() => {
    fetchPerformance();
    // Refresh every 60 seconds
    const interval = setInterval(fetchPerformance, 60000);
    return () => clearInterval(interval);
  }, [fetchPerformance]);

  const formatTime = (minutes) => {
    const mins = parseFloat(minutes);
    if (mins < 1) {
      return `${(mins * 60).toFixed(0)}s`;
    }
    return `${mins.toFixed(1)}m`;
  };

  if (loading && !data) {
    return (
      <div className="performance-page">
        <div className="performance-loading">Loading performance data...</div>
      </div>
    );
  }

  return (
    <div className="performance-page">
      <div className="performance-header">
        <h1>Production Performance</h1>
        <div className="performance-controls">
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="dept-select"
          >
            <option value="carting">Carting</option>
            <option value="wicking">Wicking</option>
            <option value="pouring">Pouring</option>
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="date-select"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <button className="refresh-btn" onClick={fetchPerformance}>
            Refresh
          </button>
        </div>
      </div>

      {data && (
        <div className="performance-content">
          {/* By Vessel Section */}
          <div className="performance-section">
            <h2>By Vessel Type</h2>
            <div className="performance-table">
              <div className="table-header">
                <div className="col-vessel">Vessel</div>
                <div className="col-units">Units</div>
                <div className="col-carts">Carts</div>
                <div className="col-time">Avg Time/Unit</div>
              </div>
              {data.byVessel?.length > 0 ? (
                data.byVessel.map((item, idx) => (
                  <div key={idx} className="table-row">
                    <div className="col-vessel">{item.vessel?.replace(/^VES-/i, '') || 'Unknown'}</div>
                    <div className="col-units">{item.units}</div>
                    <div className="col-carts">{item.carts}</div>
                    <div className="col-time">
                      <span className="time-value">{formatTime(item.avgTimePerUnit)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">No data for selected period</div>
              )}
            </div>
          </div>

          {/* By Operator Section */}
          <div className="performance-section">
            <h2>By Operator</h2>
            <div className="performance-table">
              <div className="table-header">
                <div className="col-operator">Operator</div>
                <div className="col-units">Units</div>
                <div className="col-carts">Carts</div>
                <div className="col-time">Avg Time/Unit</div>
              </div>
              {data.byOperator?.length > 0 ? (
                data.byOperator.map((item, idx) => (
                  <div key={idx} className="table-row">
                    <div className="col-operator">{item.operator}</div>
                    <div className="col-units">{item.units}</div>
                    <div className="col-carts">{item.carts}</div>
                    <div className="col-time">
                      <span className="time-value">{formatTime(item.avgTimePerUnit)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">No data for selected period</div>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          <div className="performance-summary">
            <div className="summary-card">
              <div className="summary-label">Total Units</div>
              <div className="summary-value">
                {data.byVessel?.reduce((sum, v) => sum + v.units, 0) || 0}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Carts</div>
              <div className="summary-value">
                {data.byVessel?.reduce((sum, v) => sum + v.carts, 0) || 0}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Vessel Types</div>
              <div className="summary-value">
                {data.byVessel?.length || 0}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Operators</div>
              <div className="summary-value">
                {data.byOperator?.length || 0}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CartingPerformance;
